/**
 * Shared fa-IR number, money and Jalali-date formatting.
 *
 * These started life in `pages/guarantees/components/guaranteeTheme.ts`. They
 * moved here when the home page and the pay screen needed them too: the
 * alternative was three screens formatting the same toman figure three ways.
 * That was already visible — the AccessPay banner rendered Latin digits via
 * `Number(x).toLocaleString()` while the guarantees page rendered fa-IR, so
 * the same total looked like it came from two different apps.
 *
 * guaranteeTheme re-exports these, so existing imports keep working.
 */

export const faFull = new Intl.NumberFormat('fa-IR')

/**
 * Ungrouped — for years and small counts.
 *
 * `faFull.format(1405)` renders «۱٬۴۰۵», which reads as a number in the
 * thousands rather than a year.
 */
export const faPlain = new Intl.NumberFormat('fa-IR', { useGrouping: false })

export const toman = (value: number) =>
    `${faFull.format(Number(value || 0))} تومان`

/** Just the digits, no unit — for when the caller places «تومان» itself. */
export const money = (value: number) => faFull.format(Number(value || 0))

const JALALI_MONTHS = [
    'فروردین',
    'اردیبهشت',
    'خرداد',
    'تیر',
    'مرداد',
    'شهریور',
    'مهر',
    'آبان',
    'آذر',
    'دی',
    'بهمن',
    'اسفند',
]

/**
 * «1405-04-29» becomes «۲۹ تیر ۱۴۰۵».
 *
 * The API sends the sortable YYYY-MM-DD Jalali form because it is also the
 * key that joins a timeline row to its data; the human form is built here.
 */
export const longJalali = (jalaliDate?: string | null) => {
    if (!jalaliDate) return ''
    const [year, month, day] = jalaliDate.split('-').map(Number)
    const monthName = JALALI_MONTHS[month - 1]
    if (!year || !day || !monthName) return ''
    return `${faPlain.format(day)} ${monthName} ${faPlain.format(year)}`
}

/** «۲۹ تیر» — the same date without the year, for in-year due dates. */
export const shortJalali = (jalaliDate?: string | null) => {
    if (!jalaliDate) return ''
    const [, month, day] = jalaliDate.split('-').map(Number)
    const monthName = JALALI_MONTHS[month - 1]
    if (!day || !monthName) return ''
    return `${faPlain.format(day)} ${monthName}`
}

/**
 * Human lateness/earliness for a payable row.
 *
 * Deliberately never returns an empty string for a due item — «امروز سررسید
 * است» is information, and a blank line where a date should be reads as a bug.
 */
export const dueLabel = (daysOverdue: number, isOverdue: boolean) => {
    if (isOverdue) {
        if (daysOverdue <= 0) return 'امروز سررسید است'
        return `${faPlain.format(daysOverdue)} روز تاخیر`
    }
    return ''
}
